import { Box, styled, Tab, Tabs } from '@mui/material';
import { type FC, type ReactNode, type SyntheticEvent, useState } from 'react';
import { TempCellarIcon, TempRemoteIcon } from '../../assets/custom_icons/customIcons';
import type { TemperatureDetailsType, TemperatureTrendData } from '../../models';
import { AvaniTemperatureTrendChart } from '../Charts';

interface AvaniTemperatureTrendsProps {
  devices: TemperatureDetailsType[];
  dispenseData?: TemperatureTrendData[];
}

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

const AvaniTab = styled(Tab)(
  ({ theme }) => `
  padding: 16px;
  background-color: white;
  color: ${theme.colors.avani.magenta};
  border: 2px solid ${theme.colors.avani.magenta};
  border-radius: 16px;

  &:hover {
    color: ${theme.colors.avani.magenta};
  }

  &.Mui-selected {
    color: white;
    background-color: ${theme.colors.avani.magenta};
  }
`
);

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ height: '300px' }}>{children}</Box>}
    </Box>
  );
}

function a11yProps(index: number) {
  return { id: `simple-tab-${index}`, 'aria-controls': `simple-tabpanel-${index}` };
}

export const TemperatureTrends: FC<AvaniTemperatureTrendsProps> = ({ devices, dispenseData }) => {
  const [activeTab, setActiveTab] = useState(0);

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <>
      <Tabs
        value={activeTab}
        onChange={handleChange}
        sx={{
          margin: 2,
          [`.MuiTabs-indicator`]: { backgroundColor: 'transparent', border: 'none', boxShadow: 'none' }
        }}
        variant="scrollable"
        scrollButtons={true}
      >
        {devices
          .filter((device) => device.type !== 'Bar')
          .map((device, ind) => {
            return (
              <AvaniTab
                key={ind + device.name}
                icon={
                  device.type === 'Cellar'
                    ? TempCellarIcon({ width: 36, height: 36 })
                    : TempRemoteIcon({ width: 36, height: 36 })
                }
                label={device.name}
                sx={{
                  '&.MuiTab-labelIcon.MuiTab-labelIcon': { flexDirection: 'row' },
                  '& .MuiTab-iconWrapper': { marginBottom: 0 },
                  '&&': { px: 1.5 }
                }}
                {...a11yProps(ind)}
              />
            );
          })}
      </Tabs>

      {devices.map((device, ind) => {
        return (
          <CustomTabPanel key={ind + device.name} value={activeTab} index={ind}>
            {device.temperatureTrend && (
              <AvaniTemperatureTrendChart
                data={device.temperatureTrend.map((trendData) => ({ name: device.name, data: trendData.temperature }))}
                dates={device.temperatureTrend.map((trendData) => trendData.date)}
                range={device.minTemperature && device.maxTemperature ? device : undefined}
                additionalData={dispenseData}
              />
            )}
          </CustomTabPanel>
        );
      })}
    </>
  );
};
